import { useMemo } from 'react';

import { useMappingsState } from '../providers/BulkConfigureContextProvider.tsx';
import { useGetWorkflowValidators } from '../providers/BulkCreateContextProvider.tsx';

import { defaultRequiredFields } from './useGetUnmappedRequiredFields.tsx';

/**
 * @returns Required fields specified via workflow validators that have not been mapped to a column in the bulk configure view
 */
export const useUnmappedWorkflowValidators = (): string[] => {
	const { columnToFieldMappings, optionsIssueTypeFields } = useMappingsState();
	const workflowValidators = useGetWorkflowValidators();

	return useMemo(() => {
		if (!workflowValidators || !optionsIssueTypeFields) {
			return [];
		}

		const hasValidators = workflowValidators.some((v) => v.hasValidators);
		// Check if workFlowValidators.validations,fieldValidations.fields is not empty
		const hasFieldValidations = workflowValidators.some((v) =>
			v.validations?.some((validation) =>
				validation.fieldValidations?.some((fv) => (fv.fields?.length ?? 0) > 0),
			),
		);

		if (!hasValidators) {
			return [];
		}

		// Since `validations` can legitimately be empty or malformed even when `hasValidators = true`,
		// we rely on the `hasValidators` property to determine if there are any validators.
		if (!hasFieldValidations) {
			return [''];
		}

		const mappedFieldIds = new Set(
			columnToFieldMappings
				?.map((mapping) => mapping.field?.fieldId)
				.filter((fieldId): fieldId is string => fieldId !== undefined) ?? [],
		);

		const fieldIdToNameMap = new Map(
			optionsIssueTypeFields.map((field) => [field.fieldId, field.name]),
		);

		const requiredFieldIds = workflowValidators
			.flatMap((validator) => validator.validations ?? [])
			.flatMap((validation) => validation.fieldValidations ?? [])
			.flatMap((fieldValidation) => fieldValidation.fields ?? []);

		// Filter and deduplicate using Set
		const unmappedFieldIds = new Set(
			requiredFieldIds.filter(
				(fieldId) => !mappedFieldIds.has(fieldId) && !defaultRequiredFields.includes(fieldId),
			),
		);

		// Convert to names and use fieldId as a fallback
		return Array.from(unmappedFieldIds).map((fieldId) => fieldIdToNameMap.get(fieldId) ?? fieldId);
	}, [columnToFieldMappings, workflowValidators, optionsIssueTypeFields]);
};
